import { useState, useEffect } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Input,
  Label,
} from '@pantry/ui';
import { useUpdateShoppingList } from '@pantry/api-client';

interface EditListDialogProps {
  householdId: string;
  listId: string;
  currentName: string;
  open: boolean;
  onClose: () => void;
}

/**
 * Dialog to rename an existing shopping list.
 */
export function EditListDialog({
  householdId,
  listId,
  currentName,
  open,
  onClose,
}: EditListDialogProps) {
  const { t } = useLingui();
  const [name, setName] = useState(currentName);
  const [error, setError] = useState<string | null>(null);

  const updateList = useUpdateShoppingList();

  useEffect(() => {
    if (open) {
      setName(currentName);
      setError(null);
    }
  }, [open, currentName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmed = name.trim();
    if (!trimmed) {
      setError(t`Please enter a list name.`);
      return;
    }

    try {
      await updateList.mutateAsync({
        householdId,
        listId,
        data: { name: trimmed },
      });
      onClose();
    } catch {
      setError(t`Failed to rename list. Please try again.`);
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      updateList.reset();
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            <Trans context="dialog_title">Rename List</Trans>
          </DialogTitle>
          <DialogDescription>
            <Trans>Enter a new name for this shopping list.</Trans>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-list-name">
              <Trans>List name</Trans>
            </Label>
            <Input
              id="edit-list-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              maxLength={100}
            />
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              <Trans context="action_button">Cancel</Trans>
            </Button>
            <Button type="submit" disabled={updateList.isPending}>
              {updateList.isPending ? (
                <Trans>Saving...</Trans>
              ) : (
                <Trans context="action_button">Save</Trans>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
